import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import FormGroup from '@mui/material/FormGroup';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormControl from '@mui/material/FormControl';
import FormLabel from '@mui/material/FormLabel';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';
import moment from 'moment';
import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSnackbar } from '~/containers/Snackbar/SnackbarContext';
import Spacings from '~/modules/ui-kit/spacings';
import { useMutation } from '@tanstack/react-query';
import {
  planningSupportGenerateDraft,
  planningSupportPerformPlanning,
} from '~/generated';
import { extractData } from '~/utils/query-utils';
import { useRoutes } from '~/hooks/api/useRoutes';
import messages from './messages';
import styles from './styles.module.css';

const WHOLE_WEEK = 'wholeWeek';

type PlanningSupportFormProps = {
  weekDate: string;
  onCloseMenu: () => void;
};

const PlanningSupportForm = (props: PlanningSupportFormProps) => {
  const [mode, setMode] = React.useState('plan');
  const [open, setDialogState] = React.useState(false);
  const onOpen = () => setDialogState(true);
  const onClose = () => setDialogState(false);
  const intl = useIntl();
  const { showSnackbar } = useSnackbar();
  const { data } = useRoutes();
  const planningStartMutation = useMutation({
    mutationKey: ['PlanningSupport', 'plan'],
    async mutationFn(options: {
      from: string;
      to: string;
      routes: number[];
      ignoreExistingPlan: boolean;
    }) {
      return extractData(
        planningSupportPerformPlanning({
          body: options,
        })
      );
    },
    onSuccess() {
      showSnackbar({
        message: intl.formatMessage(messages.startPlanningSuccess),
      });
      props.onCloseMenu();
      onClose();
    },
    onError() {
      showSnackbar({
        message: intl.formatMessage(messages.startPlanningFailure),
      });
      props.onCloseMenu();
    },
  });
  const planningDraftMutation = useMutation({
    mutationKey: ['PlanningSupport', 'draft'],
    async mutationFn(options: {
      from: string;
      to: string;
      routes: number[];
      ignoreExistingPlan: boolean;
    }) {
      return extractData(
        planningSupportGenerateDraft({
          body: options,
        })
      );
    },
    onSuccess(response) {
      showSnackbar({
        message: intl.formatMessage(messages.startPlanningSuccess),
      });
      if (response.link) {
        window.open(response.link);
      }
      props.onCloseMenu();
      onClose();
    },
    onError() {
      showSnackbar({
        message: intl.formatMessage(messages.startPlanningFailure),
      });
      props.onCloseMenu();
    },
  });
  const mutation =
    mode === 'plan' ? planningStartMutation : planningDraftMutation;
  const [selectedRoutes, setSelectedRoutes] = React.useState<number[]>([]);
  const [weekday, setWeekday] = React.useState<string | null>(null);
  const [ignoreExistingPlan, setIgnoreExistingPlan] = React.useState(true);
  const allRoutes = data || [];
  const handleChange: React.ChangeEventHandler<HTMLInputElement> = (event) => {
    setWeekday(event.target.value);
  };
  const handleModeChange: React.ChangeEventHandler<HTMLInputElement> = (
    event
  ) => {
    setMode(event.target.value);
  };
  return (
    <>
      <MenuItem onClick={onOpen}>
        <FormattedMessage {...messages.openDialogButton} />
      </MenuItem>
      <Dialog
        open={open}
        aria-labelledby="confirmation-modal-title"
        aria-describedby="confirmation-modal-description"
        fullWidth
      >
        <DialogTitle id="confirmation-modal-title">
          <FormattedMessage {...messages.title} />
        </DialogTitle>
        <DialogContent>
          <Spacings.Stack scale="l">
            <DialogContentText
              id="confirmation-modal-description"
              component="div"
              classes={{ root: styles.description }}
            >
              <FormattedMessage
                {...messages.description}
                values={{ p: (content) => <p>{content}</p> }}
              />
            </DialogContentText>
            <div>
              <FormControl variant="standard" component="fieldset">
                <FormLabel component="legend" id="mode">
                  <FormattedMessage {...messages.mode} />
                </FormLabel>
                <RadioGroup
                  aria-labelledby="mode"
                  name="mode"
                  value={mode}
                  onChange={handleModeChange}
                >
                  <Spacings.Stack>
                    <div>
                      <FormControlLabel
                        value="draft"
                        control={<Radio />}
                        label={<FormattedMessage {...messages.draftMode} />}
                      />
                      <Typography
                        classes={{ root: styles['radio-description'] }}
                        variant="body2"
                        color="textSecondary"
                      >
                        <FormattedMessage {...messages.draftModeDescription} />
                      </Typography>
                    </div>
                    <div>
                      <FormControlLabel
                        value="plan"
                        control={<Radio />}
                        label={<FormattedMessage {...messages.planMode} />}
                      />
                      <Typography
                        classes={{ root: styles['radio-description'] }}
                        variant="body2"
                        color="textSecondary"
                      >
                        <FormattedMessage {...messages.planModeDescription} />
                      </Typography>
                    </div>
                  </Spacings.Stack>
                </RadioGroup>
              </FormControl>
            </div>
            {mode === 'draft' ? (
              <div>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={ignoreExistingPlan}
                      onChange={() => {
                        setIgnoreExistingPlan(!ignoreExistingPlan);
                      }}
                      value={ignoreExistingPlan}
                    />
                  }
                  label={<FormattedMessage {...messages.ignoreExistingPlan} />}
                />
                <Typography
                  classes={{ root: styles['radio-description'] }}
                  variant="body2"
                  color="textSecondary"
                >
                  <FormattedMessage
                    {...messages.ignoreExistingPlanDescription}
                  />
                </Typography>
              </div>
            ) : null}
            <div>
              <FormControl variant="standard" component="fieldset">
                <FormLabel component="legend" id="weekday">
                  <FormattedMessage {...messages.weekday} />
                </FormLabel>
                <RadioGroup
                  aria-labelledby="weekday"
                  name="weekday"
                  value={weekday}
                  onChange={handleChange}
                >
                  <FormControlLabel
                    value={WHOLE_WEEK}
                    control={<Radio />}
                    label={<FormattedMessage {...messages.wholeWeek} />}
                  />
                  {moment.weekdays(true).map((day) => (
                    <FormControlLabel
                      key={day}
                      value={day}
                      control={<Radio />}
                      label={day}
                    />
                  ))}
                </RadioGroup>
              </FormControl>
            </div>
            <div>
              <FormControl variant="standard" component="fieldset">
                <FormLabel component="legend">
                  <FormattedMessage {...messages.route} />
                </FormLabel>
                <FormGroup>
                  {allRoutes.map((route) => (
                    <FormControlLabel
                      key={route.id}
                      control={
                        <Checkbox
                          checked={selectedRoutes.includes(route.id)}
                          onChange={() => {
                            if (selectedRoutes.includes(route.id)) {
                              setSelectedRoutes(
                                selectedRoutes.filter(
                                  (selectedRoute) => selectedRoute !== route.id
                                )
                              );
                            } else {
                              setSelectedRoutes([...selectedRoutes, route.id]);
                            }
                          }}
                          value={route.id}
                        />
                      }
                      label={route.name}
                    />
                  ))}
                </FormGroup>
              </FormControl>
            </div>
          </Spacings.Stack>
        </DialogContent>
        <DialogActions>
          <Button color="primary" onClick={onClose}>
            <FormattedMessage {...messages.cancel} />
          </Button>
          <Button
            onClick={() => {
              if (!weekday) {
                return;
              }
              if (selectedRoutes.length === 0) {
                return;
              }
              const unit = weekday === WHOLE_WEEK ? 'week' : 'day';
              const date =
                weekday === WHOLE_WEEK
                  ? moment(props.weekDate)
                  : moment(props.weekDate).weekday(
                      moment.weekdays(true).indexOf(weekday)
                    );
              mutation.mutate({
                from: moment(date).startOf(unit).format('YYYY-MM-DDTHH:mm:ss'),
                to: moment(date).endOf(unit).format('YYYY-MM-DDTHH:mm:ss'),
                routes: selectedRoutes,
                ignoreExistingPlan,
              });
            }}
            color="primary"
            disabled={mutation.isPending}
          >
            {mutation.isPending ? (
              <FormattedMessage {...messages.isPlanning} />
            ) : (
              <FormattedMessage {...messages.confirm} />
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default PlanningSupportForm;
